import React, { useContext } from 'react';
import { Integration } from '../UserAccess/UserContext';
import { KaEmployeeTemplate } from '../UserAccess/KaEmployeeTemplate';
import { UserTemplate } from '../UserAccess/UserTemplate';
import { useKaEmployee } from '../UserAccess/useKaEmployee';
import { useUser } from '../UserAccess/useUser';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import { usePermission } from '../UserAccess/usePermission';
import { Sidebar, SidebarLink, SidebarSection, SidebarDivider, SidebarContext } from '@ka/webcomponents/nav';

const FieldAlyticsSection = ({ employeeRoute }: { employeeRoute?: boolean }) => {
  const prefix = employeeRoute ? '/employee' : '';
  return (
    <SidebarSection
      label="FieldAlytics"
      iconSrc="/FieldAlytics_Icon.png"
      to={`${prefix}/fieldalytics/orders`}>
      <SidebarLink to={`${prefix}/fieldalytics/orders`} icon="file-ruled" label="Orders" />
      <SidebarLink to={`${prefix}/fieldalytics/siteMapping`} icon="geo-alt" label="Site Mapping" />
      <SidebarLink to={`${prefix}/fieldalytics/productMapping`} icon="diagram-3" label="Product Mapping" />
      <SidebarLink to={`${prefix}/fieldalytics/apiEventLog`} icon="braces" label="Event Log" />
      <SidebarLink to={`${prefix}/fieldalytics/settings`} icon="gear" label="Settings" />
    </SidebarSection>
  );
};

const UserLinks = () => {
  const user = useUser();

  return user.selectedBusiness == null ? null : <SelectedBusinessLinks />;
};

const SelectedBusinessLinks = () => {
  const business = useSelectedBusiness();
  const showDashboardReports = usePermission('dashboardReportsView');
  const showSharedProducts = usePermission('settingsModify') && business.usesDataSharing;
  const showIntegrations = usePermission('integrationsModify');

  const integrations: Integration[] = business.integrations ?? [];
  const hasFieldAlytics = integrations.some(i => i.name === 'FieldAlytics');
  const kahlerIntegrations = integrations
    .filter(i => i.name !== 'FieldAlytics')
    .sort((a, b) => a.name.localeCompare(b.name));

  return <>
    {showDashboardReports && <>
      <SidebarLink to="/" icon="speedometer2" label="Dashboards" end />
      <SidebarLink to="/reports" icon="bar-chart-line" label="Reports" />
    </>}

    <SidebarSection label="Orders" icon="file-ruled" to="/orders">
      <SidebarLink to="/orderTransactions" icon="file-earmark-ruled" label="Transactions" />
    </SidebarSection>

    <SidebarSection label="Products" icon="diagram-3" to="/products">
      {showSharedProducts && <SidebarLink to="/sharedproducts" icon="link-45deg" label="Shared Products" />}
      <SidebarLink to="/recipes" icon="card-list" label="Recipes" />
    </SidebarSection>

    <SidebarSection label="Purchase Orders" icon="cart" to="/purchaseOrders">
      <SidebarLink to="/suppliers" icon="box-seam" label="Suppliers" />
    </SidebarSection>

    <SidebarSection label="Logistics">
      <SidebarLink to="/customers" icon="people" label="Customers" />
      <SidebarLink to="/transports" icon="truck" label="Transports" />
      <SidebarLink to="/drivers" icon="person-badge" label="Drivers" />
      <SidebarLink to="/carriers" icon="truck-front" label="Carriers" />
      <SidebarLink to="/applicators" icon="droplet" label="Applicators" />
      <SidebarLink to="/branches" icon="pin-map" label="Branches" />
    </SidebarSection>

    {showIntegrations && <>
      <SidebarDivider />
      {hasFieldAlytics && <FieldAlyticsSection />}
      {kahlerIntegrations.map(i => <SidebarLink key={i.name} href={i.configurationUri} icon="plug" label={i.name} />)}
      <SidebarLink to="/extIntegrations" icon="plug-fill" label="External Integrations" />
    </>}
  </>;
};

const KaEmployeeLinks = () => {
  const employee = useKaEmployee();
  const hasBusiness = employee.selectedBusiness != null;
  const hasFieldAlytics = employee.selectedBusiness?.integrations.some(i => i.name === 'FieldAlytics') ?? false;

  return <>
    <SidebarLink to="/employee/business" icon="building" label="Businesses" />
    {employee.canViewNotifications && <SidebarLink to="/employee/siteNotifications" icon="braces" label="Site Notifications" />}
    {employee.canViewSiteStatus && <SidebarLink to="/employee/sitesStatus" icon="activity" label="Sites Status" />}
    {employee.canViewOrders && hasBusiness && <SidebarLink to="/employee/orders" icon="file-ruled" label="Orders" />}

    {employee.canViewIntegrations && <>
      <SidebarDivider />
      {hasFieldAlytics && <FieldAlyticsSection employeeRoute />}
      <SidebarLink to="/employee/integrations" icon="plug" label="Other Integrations" />
    </>}
  </>;
};

const KahlerLogo = ({ isCollapsed }: { isCollapsed: boolean }) =>
  <div data-testid="logo-container" className="d-flex justify-content-center">
    <a href="https://www.kahlerautomation.com" target="_blank" rel="noreferrer" aria-label="Logo" title="Kahler Automation">
      <img className={`m-2${isCollapsed ? '' : ' d-none'}`} src="/KA-RGB-color_logo-minimized.png" alt="Minimized Logo" style={{ width: '3.5rem' }} />
      <img className={`m-2${isCollapsed ? ' d-none' : ''}`} src="/KA-RGB-color_logo.png" alt="Logo" width='100' style={{ minWidth: '15rem', maxWidth: '15rem' }} />
    </a>
  </div>;

export function NavSideMenu() {
  const { isCollapsed, toggleSidebar } = useContext(SidebarContext);

  return (
    <Sidebar
      collapsed={isCollapsed}
      onToggle={toggleSidebar}
      footer={<KahlerLogo isCollapsed={isCollapsed} />}>
      <KaEmployeeTemplate>
        <KaEmployeeLinks />
      </KaEmployeeTemplate>
      <UserTemplate>
        <UserLinks />
      </UserTemplate>
    </Sidebar>
  );
}
